import { useCallback, useRef, useState, type ReactNode } from 'react'
import { Link } from 'react-router'
import { Popover } from '@carouselgg/nexus/popover'
import { Building2, ChevronDown, Plus, Trophy, User, Users } from '@carouselgg/nexus/icon'
import type { MyOrg } from '@carouselgg/auth/use-my-orgs'
import type { MyTeam } from '@carouselgg/auth/use-my-teams'
import styles from './CreateMenu.module.css'

const ADMIN_ROLES = new Set(['owner', 'admin', 'manager'])

export interface CreateMenuProps {
  teams: MyTeam[]
  orgs: MyOrg[]
  collapsed?: boolean
  onNavigate?: () => void
}

interface Entry {
  to: string
  label: string
  detail?: string
  icon: ReactNode
}

/**
 * Every way to join or start something, behind one button labelled exactly
 * that. "Create" read as making content; most people here are joining.
 *
 * Three ways in for anyone — sign up solo, start a team or club, run a league —
 * then shortcuts into the orgs the viewer already runs. A team and a club are
 * one choice: a team is a club with one team.
 */
export function CreateMenu({ teams, orgs, collapsed, onNavigate }: CreateMenuProps) {
  const [open, setOpen] = useState(false)
  const anchor = useRef<HTMLButtonElement>(null)
  const close = useCallback(() => setOpen(false), [])

  const captains = teams.filter((t) => t.role === 'captain')
  const play: Entry[] = [
    {
      to: '/compete',
      label: 'Go solo',
      detail: 'Enter a competition on your own. The league puts you on a team, or a captain picks you up.',
      icon: <User size={16} aria-hidden="true" />,
    },
    ...(captains.length > 0
      ? [{
          to: '/compete',
          label: 'Enter with your team',
          detail: captains.map((t) => t.name).slice(0, 2).join(' or '),
          icon: <Trophy size={16} aria-hidden="true" />,
        }]
      : []),
  ]
  const start: Entry[] = [
    {
      to: '/create-org?type=organization',
      label: 'Bring your squad',
      detail: 'Start a team with friends. Add more teams and it becomes a club.',
      icon: <Plus size={16} aria-hidden="true" />,
    },
    {
      to: '/create-org?type=league',
      label: 'Run the show',
      detail: 'Host a league or tournament, without the spreadsheets.',
      icon: <Building2 size={16} aria-hidden="true" />,
    },
  ]
  const run: Entry[] = orgs
    .filter((o) => ADMIN_ROLES.has(o.role))
    .slice(0, 3)
    .map((o) => ({
      to: o.type === 'league' ? `/org/${o.slug}/manage/competitions` : `/org/${o.slug}/manage/teams`,
      label: o.type === 'league' ? 'New competition' : 'Add a team or invite players',
      detail: `in ${o.name}`,
      icon: o.logoUrl
        ? <img src={o.logoUrl} alt="" className={styles.orgLogo} />
        : <Users size={16} aria-hidden="true" />,
    }))

  function item(e: Entry) {
    return (
      <Link
        key={`${e.label}-${e.detail}`}
        to={e.to}
        className={styles.item}
        onClick={() => {
          close()
          onNavigate?.()
        }}
      >
        <span className={styles.icon}>{e.icon}</span>
        <span className={styles.text}>
          <span className={styles.label}>{e.label}</span>
          {/* A real space, so a screen reader doesn't run label and detail together. */}
          {e.detail && <>{' '}<span className={styles.detail}>{e.detail}</span></>}
        </span>
      </Link>
    )
  }

  return (
    <>
      <button
        ref={anchor}
        type="button"
        className={[styles.trigger, collapsed ? styles.collapsed : ''].filter(Boolean).join(' ')}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={collapsed ? 'Join or Start' : undefined}
        onClick={() => setOpen((o) => !o)}
      >
        <span className={styles.plus} aria-hidden="true"><Plus size={16} strokeWidth={2.5} /></span>
        {!collapsed && (
          <>
            <span className={styles.triggerLabel}>Join or Start</span>
            <ChevronDown size={16} aria-hidden="true" className={[styles.chevron, open ? styles.chevronOpen : ''].filter(Boolean).join(' ')} />
          </>
        )}
      </button>
      <Popover open={open} onClose={close} anchorRef={anchor} label="Join or Start" className={styles.panel}>
        <div className={styles.group}>
          <span className={styles.heading}>Play</span>
          {play.map(item)}
        </div>
        <div className={styles.group}>
          <span className={styles.heading}>Start something</span>
          {start.map(item)}
        </div>
        {run.length > 0 && (
          <div className={styles.group}>
            <span className={styles.heading}>In what you run</span>
            {run.map(item)}
          </div>
        )}
      </Popover>
    </>
  )
}
